import { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Plus, CalendarDays, FileText, ArrowLeft, MoreHorizontal } from 'lucide-react';
import { useSprints } from '../../hooks/useSprints';
import { useDailyScrumList, useCreateDailyScrum } from '../../hooks/useDailyScrum';
import { useSprintDocuments, useCreateSprintDocument } from '../../hooks/useSprintDocuments';
import { useDocument, useUpdateDocument } from '../../hooks/useDocuments';
import { useGeneralMeetings } from '../../hooks/useGeneralMeetings';
import type { ProjectDetail } from '../../services/projectService';
import type { DailyScrumListItem } from '../../services/dailyScrumService';
import type { SprintDocumentItem } from '../../services/sprintDocumentService';
import DailyScrumDetailView from './DailyScrumDetailView';
import MeetingTypeSelectModal from './MeetingTypeSelectModal';
import DocumentTemplateSelectModal from './DocumentTemplateSelectModal';
import DocumentEditor from '../document/DocumentEditor';
import CreateGeneralMeetingModal from './CreateGeneralMeetingModal';
import Spinner from '../ui/Spinner';
import toast from 'react-hot-toast';
import { useAuthStore } from '../../store/authStore';

interface Props {
  project: ProjectDetail;
}

const WEEKDAYS = ['일', '월', '화', '수', '목', '금', '토'];

function getCalendarDays(year: number, month: number) {
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells: (number | null)[] = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

function formatDateStr(year: number, month: number, day: number): string {
  return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

export default function DailyScrumView({ project }: Props) {
  const [selectedScrumId, setSelectedScrumId] = useState<string | null>(null);
  const [selectedDoc, setSelectedDoc] = useState<{ spaceId: string; docId: string; title: string } | null>(null);
  const [currentMonth, setCurrentMonth] = useState(() => new Date());
  const [showMeetingModal, setShowMeetingModal] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [showTitleInput, setShowTitleInput] = useState(false);
  const [titleInputValue, setTitleInputValue] = useState('');
  const [pendingDocSource, setPendingDocSource] = useState<{ sourceType: 'TEMPLATE' | 'BLANK'; content?: unknown } | null>(null);
  const [pendingDate, setPendingDate] = useState<string | null>(null);

  const { data: sprints } = useSprints(project.id);
  const activeSprint = sprints?.find((s) => s.status === 'ACTIVE');

  const { data: scrumList, isLoading } = useDailyScrumList(
    project.id,
    activeSprint?.sprintId ?? '',
  );

  const createMutation = useCreateDailyScrum(
    project.id,
    activeSprint?.sprintId ?? '',
  );

  const createDocMutation = useCreateSprintDocument(
    project.id,
    activeSprint?.sprintId ?? '',
  );

  const { data: sprintDocs } = useSprintDocuments(
    project.id,
    activeSprint?.sprintId ?? '',
  );

  const todayStr = new Date().toISOString().split('T')[0];

  // 날짜 → 스크럼 매핑
  const scrumMap = useMemo(() => {
    const map = new Map<string, DailyScrumListItem>();
    if (scrumList) {
      for (const s of scrumList) {
        const dateKey = s.date.split('T')[0];
        map.set(dateKey, s);
      }
    }
    return map;
  }, [scrumList]);

  // 날짜 → 문서 매핑
  const docMap = useMemo(() => {
    const map = new Map<string, SprintDocumentItem[]>();
    if (sprintDocs) {
      for (const doc of sprintDocs) {
        if (!doc.scrumDate) continue;
        const dateKey = doc.scrumDate.split('T')[0];
        const list = map.get(dateKey) || [];
        list.push(doc);
        map.set(dateKey, list);
      }
    }
    return map;
  }, [sprintDocs]);

  const year = currentMonth.getFullYear();
  const month = currentMonth.getMonth();
  const cells = useMemo(() => getCalendarDays(year, month), [year, month]);

  const handlePrevMonth = () => setCurrentMonth(new Date(year, month - 1, 1));
  const handleNextMonth = () => setCurrentMonth(new Date(year, month + 1, 1));

  // +생성 클릭 → 모달 열기
  const handleCreateClick = (dateStr: string) => {
    setPendingDate(dateStr);
    setShowMeetingModal(true);
  };

  // 모달 옵션 핸들러
  const handleSelectDailyScrum = () => {
    if (!pendingDate) return;
    createMutation.mutate(pendingDate, {
      onSuccess: (data) => {
        toast.success('데일리 스크럼이 생성되었습니다.');
        setSelectedScrumId(data.dailyScrumId);
      },
      onError: (err: any) => toast.error(err?.response?.data?.error ?? '생성에 실패했습니다.'),
    });
  };

  const handleSelectTemplate = () => {
    setShowTemplateModal(true);
  };

  const handleTemplateSelected = (template: { name: string; content?: unknown }) => {
    setPendingDocSource({ sourceType: 'TEMPLATE', content: template.content });
    setTitleInputValue(template.name);
    setShowTitleInput(true);
  };

  const handleSelectBlankPage = () => {
    setPendingDocSource({ sourceType: 'BLANK' });
    const dateFormatted = pendingDate
      ? new Date(pendingDate).toLocaleDateString('ko-KR', { month: 'long', day: 'numeric' })
      : '';
    setTitleInputValue(`${dateFormatted} 회의록`);
    setShowTitleInput(true);
  };

  const handleTitleConfirm = () => {
    if (!pendingDate || !pendingDocSource || !titleInputValue.trim()) return;
    createDocMutation.mutate(
      {
        scrumDate: pendingDate,
        sourceType: pendingDocSource.sourceType,
        title: titleInputValue.trim(),
        content: pendingDocSource.content,
      },
      {
        onSuccess: (data) => {
          setShowTitleInput(false);
          setSelectedDoc({ spaceId: data.spaceId, docId: data.id, title: data.title });
          toast.success('문서가 생성되었습니다.');
        },
        onError: (err: any) => toast.error(err?.response?.data?.error ?? '문서 생성에 실패했습니다.'),
      },
    );
  };

  // 문서 인라인 뷰 분기
  if (selectedDoc) {
    return <InlineDocumentView spaceId={selectedDoc.spaceId} docId={selectedDoc.docId} title={selectedDoc.title} onBack={() => setSelectedDoc(null)} />;
  }

  // 상세 뷰 분기
  if (selectedScrumId && activeSprint) {
    return (
      <DailyScrumDetailView
        project={project}
        sprintId={activeSprint.sprintId}
        dailyScrumId={selectedScrumId}
        onBack={() => setSelectedScrumId(null)}
      />
    );
  }

  // 활성 스프린트 없음
  if (!activeSprint) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center py-20">
        <CalendarDays className="size-12 text-gray-300 mb-4" />
        <p className="text-sm text-gray-500">활성 스프린트가 없습니다.</p>
        <p className="text-sm text-gray-400 mt-1">백로그에서 스프린트를 시작해주세요.</p>
      </div>
    );
  }

  if (isLoading) return <Spinner />;

    // h-full로 부모 컨테이너 높이를 채우되, 내부에서 자체 스크롤 처리.
    // 부모 overflow-y-auto 래퍼는 min-h-0가 없어 페이지 스크롤이 활성화되지 않음 → 자식이 책임.
    // ⚠️ flex-col을 쓰면 자식들이 기본 flex-shrink:1로 압축되어 캘린더가 짧아진다.
    //    block 레이아웃 + 자식들의 자연 높이 유지 + overflow-y-auto로 스크롤.
  return (
    <div className="h-full overflow-y-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-base font-semibold text-gray-900">
            {activeSprint.name} <span className="text-gray-400 font-normal">— 데일리 스크럼</span>
          </h2>
          {activeSprint.startDate && activeSprint.endDate && (
            <p className="text-sm text-gray-400 mt-0.5">
              {activeSprint.startDate.split('T')[0]} ~ {activeSprint.endDate.split('T')[0]}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={handlePrevMonth} className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors">
            <ChevronLeft className="size-4 text-gray-600" />
          </button>
          <span className="text-sm font-medium text-gray-700 min-w-[100px] text-center">
            {year}년 {month + 1}월
          </span>
          <button onClick={handleNextMonth} className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors">
            <ChevronRight className="size-4 text-gray-600" />
          </button>
        </div>
      </div>

      {/* Calendar Grid — 행마다 최소 110px 보장으로 한 달 6행이 항상 노출됨 (압축 방지) */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden flex flex-col">
        {/* Weekday header */}
        <div className="grid grid-cols-7 border-b border-gray-200">
          {WEEKDAYS.map((day, i) => (
            <div
              key={day}
              className={`py-2 text-center text-sm font-medium ${
                i === 0 ? 'text-red-400' : i === 6 ? 'text-blue-400' : 'text-gray-500'
              }`}
            >
              {day}
            </div>
          ))}
        </div>

        {/* Date cells */}
        <div className="grid grid-cols-7" style={{ gridAutoRows: 'minmax(110px, 1fr)' }}>
          {cells.map((day, idx) => {
            if (day === null) {
              return <div key={`empty-${idx}`} className="border-b border-r border-gray-100 bg-gray-50/30" />;
            }

            const dateStr = formatDateStr(year, month, day);
            const scrum = scrumMap.get(dateStr);
            const docs = docMap.get(dateStr) ?? [];
            const isToday = dateStr === todayStr;
            const isFuture = dateStr > todayStr;
            const canCreate = isToday || isFuture;
            const dayOfWeek = (idx % 7);
            const scrumLabel = `데일리 스크럼_${dateStr.slice(2).replace(/-/g, '')}`;

            return (
              <div
                key={dateStr}
                className={`border-b border-r border-gray-100 p-1.5 flex flex-col transition-colors ${
                  isToday ? 'bg-blue-50/40' : ''
                }`}
              >
                <div className="flex items-center justify-between mb-0.5">
                  <span
                    className={`text-sm ${
                      isToday
                        ? 'font-bold text-blue-600'
                        : dayOfWeek === 0
                          ? 'text-red-400'
                          : dayOfWeek === 6
                            ? 'text-blue-400'
                            : 'text-gray-500'
                    }`}
                  >
                    {day}
                  </span>
                  {canCreate && (
                    <button
                      onClick={() => handleCreateClick(dateStr)}
                      className="text-gray-300 hover:text-blue-500 transition-colors"
                    >
                      <Plus className="size-3.5" />
                    </button>
                  )}
                </div>

                <CalendarCellItems
                  scrum={scrum}
                  scrumLabel={scrumLabel}
                  docs={docs}
                  onScrumClick={(id) => setSelectedScrumId(id)}
                  onDocClick={(doc) => setSelectedDoc({ spaceId: doc.spaceId, docId: doc.id, title: doc.title })}
                />
              </div>
            );
          })}
        </div>
      </div>

      {/* CHG-130 REQ-DHR-021: 회의록 세그먼트 — 일반 회의록 */}
      <GeneralMeetingsSegment project={project} />

      {/* 회의 유형 선택 모달 */}
      <MeetingTypeSelectModal
        isOpen={showMeetingModal}
        onClose={() => setShowMeetingModal(false)}
        onSelectDailyScrum={handleSelectDailyScrum}
        onSelectTemplate={handleSelectTemplate}
        onSelectBlankPage={handleSelectBlankPage}
      />

      <DocumentTemplateSelectModal
        isOpen={showTemplateModal}
        projectId={project.id}
        onClose={() => setShowTemplateModal(false)}
        onSelect={handleTemplateSelected}
      />

      {/* 제목 입력 모달 */}
      {showTitleInput && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={() => setShowTitleInput(false)}>
          <div className="bg-white rounded-xl shadow-xl w-[400px] max-w-[95vw] p-5 space-y-4" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-sm font-semibold text-gray-900">문서 제목 입력</h3>
            <input
              value={titleInputValue}
              onChange={(e) => setTitleInputValue(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && titleInputValue.trim()) handleTitleConfirm(); }}
              placeholder="제목을 입력하세요"
              autoFocus
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg outline-none focus:ring-2 focus:ring-primary-200"
            />
            <div className="flex justify-end gap-2">
              <button onClick={() => setShowTitleInput(false)} className="px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">
                취소
              </button>
              <button
                onClick={handleTitleConfirm}
                disabled={!titleInputValue.trim() || createDocMutation.isPending}
                className="px-3 py-1.5 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg disabled:opacity-50"
              >
                {createDocMutation.isPending ? '생성 중...' : '생성'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── 캘린더 셀 아이템 (최대 2개 + 더보기) ──
const MAX_VISIBLE = 2;

interface CellItem {
  key: string;
  type: 'scrum' | 'doc';
  label: string;
  onClick: () => void;
}

function CalendarCellItems({
  scrum,
  scrumLabel,
  docs,
  onScrumClick,
  onDocClick,
}: {
  scrum: DailyScrumListItem | undefined;
  scrumLabel: string;
  docs: SprintDocumentItem[];
  onScrumClick: (id: string) => void;
  onDocClick: (doc: SprintDocumentItem) => void;
}) {
  const [showMore, setShowMore] = useState(false);
  const [popoverPos, setPopoverPos] = useState<{ top: number; left: number } | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!showMore) return;
    const handle = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node) &&
          triggerRef.current && !triggerRef.current.contains(e.target as Node)) {
        setShowMore(false);
      }
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [showMore]);

  const handleToggleMore = () => {
    if (showMore) { setShowMore(false); return; }
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPopoverPos({ top: rect.top, left: rect.left });
    }
    setShowMore(true);
  };

  const allItems: CellItem[] = [];
  if (scrum) {
    allItems.push({ key: `scrum-${scrum.dailyScrumId}`, type: 'scrum', label: scrumLabel, onClick: () => onScrumClick(scrum.dailyScrumId) });
  }
  for (const doc of docs) {
    allItems.push({ key: `doc-${doc.id}`, type: 'doc', label: doc.title, onClick: () => onDocClick(doc) });
  }

  const visible = allItems.slice(0, MAX_VISIBLE);
  const hidden = allItems.slice(MAX_VISIBLE);

  return (
    <div className="flex-1 flex flex-col gap-0.5 overflow-hidden">
      {visible.map((item) => (
        <button
          key={item.key}
          onClick={item.onClick}
          className={`flex items-center gap-1 text-left rounded px-1 py-0.5 transition-colors truncate ${
            item.type === 'scrum' ? 'hover:bg-blue-50' : 'hover:bg-purple-50'
          }`}
        >
          {item.type === 'scrum' ? (
            <span className="size-1.5 rounded-full bg-blue-500 shrink-0" />
          ) : (
            <FileText className="size-3 text-purple-400 shrink-0" />
          )}
          <span className={`text-[11px] truncate ${item.type === 'scrum' ? 'text-blue-700' : 'text-purple-700'}`}>
            {item.label}
          </span>
        </button>
      ))}
      {hidden.length > 0 && (
        <>
          <button
            ref={triggerRef}
            onClick={handleToggleMore}
            className="flex items-center gap-0.5 text-[11px] text-gray-400 hover:text-gray-600 px-1 transition-colors"
          >
            <MoreHorizontal className="size-3" />
            +{hidden.length}건 더보기
          </button>
          {showMore && popoverPos && (
            <div
              ref={popoverRef}
              className="fixed z-[9999] bg-white border border-gray-200 rounded-lg shadow-xl p-1.5 min-w-[200px] max-h-[240px] overflow-y-auto"
              style={{ top: popoverPos.top - 4, left: popoverPos.left, transform: 'translateY(-100%)' }}
            >
              <p className="text-[11px] text-gray-400 px-2 py-1 border-b border-gray-100 mb-1">전체 항목</p>
              {allItems.map((item) => (
                <button
                  key={item.key}
                  onClick={() => { item.onClick(); setShowMore(false); }}
                  className={`w-full flex items-center gap-1.5 text-left rounded px-2 py-1.5 transition-colors truncate text-sm ${
                    item.type === 'scrum' ? 'hover:bg-blue-50 text-blue-700' : 'hover:bg-purple-50 text-purple-700'
                  }`}
                >
                  {item.type === 'scrum' ? (
                    <span className="size-1.5 rounded-full bg-blue-500 shrink-0" />
                  ) : (
                    <FileText className="size-3 text-purple-400 shrink-0" />
                  )}
                  <span className="truncate">{item.label}</span>
                </button>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── 인라인 문서 편집기 ──
function InlineDocumentView({ spaceId, docId, title: initialTitle, onBack }: { spaceId: string; docId: string; title: string; onBack: () => void }) {
  const { data: doc, isLoading } = useDocument(spaceId, docId);
  const updateDoc = useUpdateDocument();
  const [title, setTitle] = useState(initialTitle);
  const titleTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const contentTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const handleTitleChange = useCallback((newTitle: string) => {
    setTitle(newTitle);
    if (titleTimeoutRef.current) clearTimeout(titleTimeoutRef.current);
    titleTimeoutRef.current = setTimeout(() => {
      updateDoc.mutate({ spaceId, documentId: docId, data: { title: newTitle } });
    }, 800);
  }, [spaceId, docId, updateDoc]);

  const handleContentChange = useCallback((blocks: any[]) => {
    if (contentTimeoutRef.current) clearTimeout(contentTimeoutRef.current);
    contentTimeoutRef.current = setTimeout(() => {
      updateDoc.mutate({ spaceId, documentId: docId, data: { content: blocks } });
    }, 1000);
  }, [spaceId, docId, updateDoc]);

  if (isLoading) return <Spinner />;

  return (
    <div className="h-full flex flex-col">
      <div className="flex items-center gap-3 pb-3 border-b border-gray-200 shrink-0">
        <button onClick={onBack} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 transition-colors">
          <ArrowLeft className="size-4" />
          뒤로
        </button>
        <input
          value={title}
          onChange={(e) => handleTitleChange(e.target.value)}
          className="flex-1 text-base font-semibold text-gray-900 outline-none border-b border-transparent hover:border-gray-300 focus:border-blue-500 px-1 py-0.5 bg-transparent"
          placeholder="제목을 입력하세요"
        />
        {updateDoc.isPending && <span className="text-[11px] text-gray-400">저장 중...</span>}
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto py-4">
        <DocumentEditor
          initialContent={(doc?.content as any[]) ?? []}
          onContentChange={handleContentChange}
          editable={true}
          spaceId={spaceId}
        />
      </div>
    </div>
  );
}

// ── CHG-130 REQ-DHR-021/022: 일반 회의록 세그먼트 ──
function GeneralMeetingsSegment({ project }: { project: ProjectDetail }) {
  const navigate = useNavigate();
  const userId = useAuthStore((s) => s.user?.id);
  const userRole = useAuthStore((s) => s.user?.role);
  const myMembership = project.members.find((m) => m.userId === userId);
  const isStakeholder = myMembership?.projectRole === 'STAKEHOLDER' && userRole !== 'SUPER_ADMIN';
  const canCreate = !!myMembership && !isStakeholder;

  const [showModal, setShowModal] = useState(false);
  const { data, isLoading } = useGeneralMeetings(project.id, 20);

  // 일반 회의록 생성에 필요한 spaceId는 첫 회의록 조회로 알 수 있지만, 없으면 별도 호출 필요.
  // listGeneralMeetings 응답에 folderId가 포함되어 있으므로 spaceId는 다른 채널로 얻어야 함.
  // 임시: 첫 docs의 spaceId 추론은 불가하므로 GET 응답에서 folderId만 사용. 모달은 spaceId 필요.
  // → 모달 오픈 시 별도 API 없이 useTeamSpace로 가져오기는 비효율. 본 segment는 인라인에 spaceId 알기 위해
  //   listGeneralMeetings 응답을 확장하거나 모달이 자체 조회.
  // 여기서는 동일 응답을 활용하되, 백엔드가 folderId만 노출하므로 모달에 folderId만 전달하고
  // spaceId는 모달에서 별도 호출. 그러나 변경 범위 최소화를 위해 listGeneralMeetings 응답에
  // spaceId 포함 보강은 다음 스텝으로 미루고, 현재는 useTeamSpace로 spaceId를 얻는다.
  // (구현은 모달 컴포넌트 내부에서 spaceId fetch — 단, 본 CHG는 빠른 진입 위해 prop 우선)

  const handleCreate = () => setShowModal(true);
  const handleCreated = (newDocId: string) => {
    navigate(`/projects/${project.projectKey}/documents/${newDocId}`);
  };

  const dateLabel = (iso: string | null) => {
    if (!iso) return '';
    const d = new Date(iso);
    return `${d.getMonth() + 1}/${d.getDate()}`;
  };

  return (
    <div className="mt-4">
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">일반 회의록</h3>
            <p className="text-sm text-gray-400">데일리 스크럼 외 회의록을 모아 볼 수 있습니다</p>
          </div>
          <button
            onClick={handleCreate}
            disabled={!canCreate || !data?.folderId}
            className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
            title={isStakeholder ? '조회만 가능합니다' : undefined}
          >
            <Plus className="size-3.5" />
            일반 회의록
          </button>
        </div>

        <div className="px-4 py-3">
          {isLoading ? (
            <div className="flex justify-center py-6"><Spinner className="size-5" /></div>
          ) : !data || data.documents.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-6">
              아직 일반 회의록이 없습니다. {canCreate ? '첫 회의록을 작성해보세요.' : ''}
            </p>
          ) : (
            <ul className="divide-y divide-gray-100">
              {data.documents.map((doc) => (
                <li key={doc.id}>
                  <button
                    type="button"
                    onClick={() => navigate(`/projects/${project.projectKey}/documents/${doc.id}`)}
                    className="w-full flex items-center gap-2 py-2 hover:bg-primary-50 rounded-md text-left"
                  >
                    <FileText className="size-4 text-gray-400 shrink-0" />
                    <span className="text-sm text-gray-900 truncate flex-1">{doc.title}</span>
                    {(doc.sourceType === 'TEMPLATE' || doc.sourceType === 'BLANK') && (
                      <span className="text-[11px] font-medium px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 shrink-0">
                        {doc.sourceType === 'TEMPLATE' ? '템플릿' : '빈 문서'}
                      </span>
                    )}
                    {doc.scrumDate && (
                      <span className="text-sm text-gray-400 shrink-0">{dateLabel(doc.scrumDate)}</span>
                    )}
                    <span className="text-sm text-gray-400 shrink-0 ml-1">{doc.creator.name}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {showModal && data?.folderId && data?.spaceId && (
        <CreateGeneralMeetingModal
          open
          onClose={() => setShowModal(false)}
          project={project}
          spaceId={data.spaceId}
          generalMeetingFolderId={data.folderId}
          onCreated={handleCreated}
        />
      )}
    </div>
  );
}
